import React, { useEffect, useState } from 'react';
import { db } from '../../firebase-client';
import { useAuth } from '../../auth/AuthContext';
import { collection, doc, getDoc } from 'firebase/firestore';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { buildMirrorCalendarEvents } from '../../utils/calendarMirrorEvents';
import StandardCalendarView from '../../components/calendar/StandardCalendarView';
import CalendarSourceSwitcher from '../../components/calendar/CalendarSourceSwitcher';
import EventFormModal from '../../components/forms/teamForms/eventFormModal';
import { buildEventModalStateFromMirrorEvent, isAdminTeamRole } from '../../components/calendar/mirrorEventModal';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';
import { readOfflineContentCache, writeOfflineContentCache } from '../../utils/offlineContentCache';
import { buildEventCopyInitialData } from '../../components/forms/teamForms/events/eventCopy';

const CACHE_SCOPE = 'calendar';
async function loadTeamAdminAccess(teamId, userId) {
  if (!teamId || !userId) return { isEventAdmin: false, canModerateEventComments: false, canManageEventRefunds: false };
  try {
    const [teamSnap, assignmentSnap] = await Promise.all([
      getDoc(doc(db, 'teams', teamId)),
      getDoc(doc(db, 'teams', teamId, 'adminAssignments', userId)).catch(() => null),
    ]);
    const teamData = teamSnap.exists() ? teamSnap.data() || {} : {};
    const fullAdmin = Array.isArray(teamData.admins) && teamData.admins.includes(userId);
    const assignedKeys = assignmentSnap?.exists() && Array.isArray(assignmentSnap.data()?.permissionKeys)
      ? assignmentSnap.data().permissionKeys
      : [];
    return {
      isEventAdmin: fullAdmin || assignedKeys.includes('events.manage'),
      canModerateEventComments: fullAdmin || assignedKeys.includes('communications.manage'),
      canManageEventRefunds: fullAdmin || assignedKeys.includes('finance.manage'),
    };
  } catch {
    return { isEventAdmin: false, canModerateEventComments: false, canManageEventRefunds: false };
  }
}
async function loadRsvpSeriesResponseSettings(rows) {
  const teamIds = Array.from(new Set(
    (rows || [])
      .map((row) => row?.data?.teamId)
      .filter((teamId) => typeof teamId === 'string' && teamId.trim() !== '')
  ));
  const entries = await Promise.all(teamIds.map(async (teamId) => {
    try {
      const snap = await getDoc(doc(db, 'teams', teamId));
      return [teamId, snap.exists() ? snap.data()?.eventSettings?.rsvpSeriesResponsesEnabled !== false : true];
    } catch {
      return [teamId, true];
    }
  }));
  return Object.fromEntries(entries);
}
export default function UserCalendarPage() {
  const {
    user
  } = useAuth();
  const [events, setEvents] = useState([]);
  const [teamRoles, setTeamRoles] = useState({});
  const [adminAccessByTeam, setAdminAccessByTeam] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const onlineRefreshSignal = useOnlineRefreshSignal();
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    const eventCacheParts = [user.uid, CACHE_SCOPE, 'userRows'];
    const cachedRows = readOfflineContentCache(eventCacheParts, []);
    if (cachedRows.length > 0) {
      loadRsvpSeriesResponseSettings(cachedRows).then((rsvpSeriesResponsesByTeam) => {
        if (cancelled) return;
        setEvents(buildMirrorCalendarEvents(cachedRows, {
          maxOccurrences: 24,
          seriesMode: 'full',
          rsvpSeriesResponsesByTeam
        }));
      });
    }
    const ref = collection(db, `privateUsers/${user.uid}/calendar`);
    const unsub = safeOnSnapshot(ref, async snap => {
      const rows = snap.docs.map(d => ({
        id: d.id,
        data: d.data() || {}
      }));
      if (rows.length === 0 && snap.metadata?.fromCache && cachedRows.length > 0) return;
      const rsvpSeriesResponsesByTeam = await loadRsvpSeriesResponseSettings(rows);
      if (cancelled) return;
      setEvents(buildMirrorCalendarEvents(rows, {
        maxOccurrences: 24,
        seriesMode: 'full',
        rsvpSeriesResponsesByTeam
      }));
      if (rows.length > 0 || !snap.metadata?.fromCache) {
        writeOfflineContentCache(eventCacheParts, rows);
      }
    }, err => void 0);
    return () => {
      cancelled = true;
      unsub && unsub();
    };
  }, [onlineRefreshSignal, user?.uid]);
  useEffect(() => {
    if (!user?.uid) {
      setTeamRoles({});
      return;
    }
    (async () => {
      const roleCacheParts = [user.uid, CACHE_SCOPE, 'teamRoles'];
      const cachedRoles = readOfflineContentCache(roleCacheParts, {});
      if (Object.keys(cachedRoles).length > 0) setTeamRoles(cachedRoles);
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (!userSnap.exists()) {
          if (!userSnap.metadata?.fromCache) setTeamRoles({});
          return;
        }
        const nextRoles = userSnap.data()?.team_roles || {};
        if (Object.keys(nextRoles).length === 0 && userSnap.metadata?.fromCache && Object.keys(cachedRoles).length > 0) {
          return;
        }
        setTeamRoles(nextRoles);
        if (Object.keys(nextRoles).length > 0 || !userSnap.metadata?.fromCache) {
          writeOfflineContentCache(roleCacheParts, nextRoles);
        }
      } catch {
        if (Object.keys(cachedRoles).length === 0) setTeamRoles({});
      }
    })();
  }, [onlineRefreshSignal, user?.uid]);
  useEffect(() => {
    if (!user?.uid || !selectedTeamId) return;
    let cancelled = false;
    loadTeamAdminAccess(selectedTeamId, user.uid).then((access) => {
      if (cancelled) return;
      setAdminAccessByTeam((current) => ({
        ...current,
        [selectedTeamId]: access,
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [selectedTeamId, user?.uid]);
  const modalIsOpen = !!(selectedEvent && selectedTeamId);
  const selectedAdminAccess = selectedTeamId ? adminAccessByTeam[selectedTeamId] : null;
  const modalIsAdmin = isAdminTeamRole(teamRoles?.[selectedTeamId]) || Boolean(selectedAdminAccess?.isEventAdmin);
  const canModerateEventComments = Boolean(selectedAdminAccess?.canModerateEventComments || isAdminTeamRole(teamRoles?.[selectedTeamId]));
  const canManageEventRefunds = Boolean(selectedAdminAccess?.canManageEventRefunds || isAdminTeamRole(teamRoles?.[selectedTeamId]));
  return <>
      <StandardCalendarView title="Your Personal Committed Calendar" events={events} toolbarStart={<CalendarSourceSwitcher compact currentScope="user" />} selectable={false} onSelectEvent={event => {
      const next = buildEventModalStateFromMirrorEvent(event);
      if (!next.teamId || !next.initialData) return;
      setSelectedTeamId(next.teamId);
      setSelectedEvent(next.initialData);
    }} />

      {modalIsOpen && <EventFormModal key={selectedEvent._copyToken || selectedEvent.id || selectedEvent.originalId || 'new-event'} teamId={selectedTeamId} isOpen={true} onClose={() => {
      setSelectedEvent(null);
      setSelectedTeamId(null);
    }} onCopy={(event) => setSelectedEvent(buildEventCopyInitialData(event))} initialData={selectedEvent} readOnly={!modalIsAdmin} userId={user?.uid || ''} isAdmin={modalIsAdmin} canModerateEventComments={canModerateEventComments} canManageEventRefunds={canManageEventRefunds} teamBrand={{}} calendarSurface="personal" />}
    </>;
}
